// redux/slices/pushTokensSlice.js
"use client";

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getServerUrl, getAuthHeaders } from "../../Utils/serverSelector";
import { resetStore } from "./resetStore";

/* ============== HELPERS ============== */

const authJsonConfig = async () => {
  const headers = await getAuthHeaders();
  return {
    headers,
    withCredentials: true,
    validateStatus: () => true,
  };
};

/* ============== THUNKS ============== */

// Private (с JWT/headers)
export const fetchPushToken = createAsyncThunk(
  "pushTokens/fetchPushToken",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const cfg = await authJsonConfig();
      const { data, status } = await axios.get(
        `${getServerUrl()}/push-tokens/${uid}`,
        cfg
      );

      if (status === 404) return null;

      if (status !== 200) {
        throw new Error(
          data?.message || data?.error || `Failed to fetch push token (HTTP ${status})`
        );
      }

      return data;
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message || "Failed to fetch push token");
    }
  }
);

// Public (без авторизации)
export const fetchPublicPushToken = createAsyncThunk(
  "pushTokens/fetchPublicPushToken",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");
      const base = (getServerUrl() || "").replace(/\/$/, "");
      const { data, status } = await axios.get(
        `${base}/public/${uid}/push-token`,
        {
          headers: { Accept: "application/json" },
          validateStatus: () => true,
        }
      );

      if (status === 404) return null;

      if (status !== 200) {
        throw new Error(
          data?.message || data?.error || `Failed to fetch public push token (HTTP ${status})`
        );
      }

      return data;
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message || "Failed to fetch public push token");
    }
  }
);

/* ============== STATE ============== */

const initialState = {
  tokenData: null,
  status: "idle",
  error: null,
};

/* ============== SLICE ============== */

const pushTokensSlice = createSlice({
  name: "pushTokens",
  initialState,
  reducers: {
    // ✅ НОВЫЙ: напрямую кладём токен в стор без HTTP-запроса
    // Используется в CancelAppointment после fetchPublicAppointmentPage
    setPushTokenDirectly: (state, action) => {
      // action.payload — строка токена или null
      state.tokenData = action.payload ? { token: action.payload } : null;
      state.status = "succeeded";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ---- PRIVATE ---- */
      .addCase(fetchPushToken.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPushToken.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.tokenData = action.payload || null;
      })
      .addCase(fetchPushToken.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message || "Failed to fetch push token";
        state.tokenData = null;
      })

      /* ---- PUBLIC ---- */
      .addCase(fetchPublicPushToken.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPublicPushToken.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.tokenData = action.payload || null;
      })
      .addCase(fetchPublicPushToken.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message || "Failed to fetch public push token";
        state.tokenData = null;
      })

      /* ---- RESET ---- */
      .addCase(resetStore, () => initialState);
  },
});

export const { setPushTokenDirectly } = pushTokensSlice.actions;
export default pushTokensSlice.reducer;